<div class="config-company-container">
  <div class="page-header">
    <h1>
      <tui-icon icon="mat::settings"></tui-icon>
      Config Company
    </h1>
    <p>
      Complete SAP field mapping and template configuration for your company.
    </p>
  </div>

  <!-- Progress Steps -->
  <div class="config-steps">
    <div
      class="config-step"
      [class.active]="currentStep === 1"
      [class.completed]="currentStep > 1"
      (click)="showStep(1)">
      <div class="step-number">1</div>
      <div class="step-info">
        <strong>Company & Mappings</strong>
        <div>Configure field mappings</div>
      </div>
    </div>

    <div
      class="config-step"
      [class.active]="currentStep === 2"
      [class.completed]="currentStep > 2"
      [class.disabled]="!canProceedToStep2"
      (click)="canProceedToStep2 && showStep(2)">
      <div class="step-number">2</div>
      <div class="step-info">
        <strong>Optional Fields</strong>
        <div>Select generic fields</div>
      </div>
    </div>

    <div
      class="config-step"
      [class.active]="currentStep === 3"
      [class.completed]="currentStep > 3"
      [class.disabled]="!canProceedToStep3"
      (click)="canProceedToStep3 && showStep(3)">
      <div class="step-number">3</div>
      <div class="step-info">
        <strong>Template Generation</strong>
        <div>Create universal template</div>
      </div>
    </div>

    <div
      class="config-step"
      [class.active]="currentStep === 4"
      [class.disabled]="!canProceedToStep4"
      (click)="canProceedToStep4 && showStep(4)">
      <div class="step-number">4</div>
      <div class="step-info">
        <strong>Test & Deploy</strong>
        <div>Test and activate</div>
      </div>
    </div>
  </div>

  <!-- Progress Bar -->
  <div class="progress-bar">
    <div class="progress-fill" [style.width.%]="progressPercentage"></div>
  </div>

  <!-- Step 1: Company Selection & Field Mappings -->
  <div *ngIf="currentStep === 1" class="step-content">
    <div class="section-header">
      <tui-icon icon="mat::business"></tui-icon>
      Step 1: Company Selection & Field Mappings
    </div>

    <!-- Company Selector -->
    <div class="company-selector">
      <label>
        <strong>Select Company:</strong>
      </label>
      <tui-select
        [(ngModel)]="selectedCompanyId"
        [disabled]="loading"
        [stringify]="stringifyCompany"
        placeholder="Choose a company">
        <tui-data-list *tuiDataList>
          <button
            *ngFor="let company of companies"
            tuiOption
            [value]="company.id">
            {{ company.name }}
          </button>
        </tui-data-list>
      </tui-select>

      <button
        tuiButton
        appearance="secondary"
        [disabled]="!selectedCompanyId || fetchingData"
        (click)="loadCompanyData()">
        <tui-icon icon="mat::refresh"></tui-icon>
        Load Company Data
      </button>
    </div>

    <!-- Basic message for field mappings -->
    <div class="field-mapping-info">
      <p>Select a company and load data to configure field mappings.</p>
      <p *ngIf="fetchingData">Loading company data...</p>
      <p *ngIf="fieldMappings.length > 0">
        {{ fieldMappings.length }} field mappings loaded.
      </p>
    </div>

    <!-- Step Actions -->
    <div class="step-actions">
      <div></div>
      <button
        tuiButton
        appearance="primary"
        [disabled]="!canProceedToStep2"
        (click)="showStep(2)">
        <tui-icon icon="mat::arrow_forward"></tui-icon>
        Next: Optional Fields
      </button>
    </div>
  </div>

  <!-- Step 2: Optional Fields -->
  <div *ngIf="currentStep === 2" class="step-content">
    <div class="section-header">
      <tui-icon icon="mat::add_circle"></tui-icon>
      Step 2: Optional Generic Fields
    </div>

    <p class="step-description">
      Select additional generic fields to include in your templates. These are
      optional and can be used for enhanced filtering.
    </p>

    <div class="optional-fields-grid">
      <div
        *ngFor="let field of optionalFields"
        class="optional-field-card"
        [class.enabled]="field.enabled">
        <div class="field-header">
          <tui-checkbox [(ngModel)]="field.enabled">
            <strong>{{ field.label }}</strong>
          </tui-checkbox>
        </div>

        <div class="field-description">
          {{ field.description }}
        </div>

        <div *ngIf="field.enabled" class="field-mapping">
          <label>SAP Field Path:</label>
          <tui-input
            [(ngModel)]="field.sap_field_path"
            placeholder="Enter SAP field path">
          </tui-input>
        </div>
      </div>
    </div>

    <!-- Step Actions -->
    <div class="step-actions">
      <button tuiButton appearance="secondary" (click)="showStep(1)">
        <tui-icon icon="mat::arrow_back"></tui-icon>
        Back: Company
      </button>
      <button tuiButton appearance="primary" (click)="showStep(3)">
        <tui-icon icon="mat::arrow_forward"></tui-icon>
        Next: Generate Template
      </button>
    </div>
  </div>

  <!-- Step 3: Template Generation -->
  <div *ngIf="currentStep === 3" class="step-content">
    <div class="section-header">
      <tui-icon icon="mat::auto_awesome"></tui-icon>
      Step 3: Generate Universal Search Template
    </div>

    <p class="step-description">
      Create a universal search template with OR conditions for itemcode,
      barcodes, and primary identifier across all warehouses.
    </p>

    <!-- Template Configuration -->
    <div class="template-config">
      <div class="template-form">
        <label>Template Name:</label>
        <tui-input [(ngModel)]="templateName" placeholder="Enter template name">
        </tui-input>

        <label>
          <tui-checkbox [(ngModel)]="includeOptionalFields">
            Include Optional Fields
          </tui-checkbox>
        </label>
      </div>

      <button
        tuiButton
        appearance="primary"
        [disabled]="!templateName.trim() || generatingTemplate"
        (click)="generateTemplate()">
        <tui-icon icon="mat::build"></tui-icon>
        Generate Template
      </button>
    </div>

    <!-- SQL Preview -->
    <div *ngIf="generatedSql" class="sql-preview">
      <h3>
        <tui-icon icon="mat::code"></tui-icon>
        Generated SQL Query
      </h3>
      <textarea
        class="sql-code"
        [value]="generatedSql"
        [rows]="10"
        [readonly]="true">
      </textarea>
    </div>

    <!-- Step Actions -->
    <div class="step-actions">
      <button tuiButton appearance="secondary" (click)="showStep(2)">
        <tui-icon icon="mat::arrow_back"></tui-icon>
        Back: Optional Fields
      </button>
      <button
        tuiButton
        appearance="primary"
        [disabled]="!canProceedToStep4"
        (click)="showStep(4)">
        <tui-icon icon="mat::arrow_forward"></tui-icon>
        Next: Test & Deploy
      </button>
    </div>
  </div>

  <!-- Step 4: Test & Deploy -->
  <div *ngIf="currentStep === 4" class="step-content">
    <div class="section-header">
      <tui-icon icon="mat::check_circle"></tui-icon>
      Step 4: Test & Deploy Template
    </div>

    <p class="step-description">
      Test your generated template with sample data and deploy it when
      satisfied.
    </p>

    <!-- Test Configuration -->
    <div class="test-config">
      <button
        tuiButton
        appearance="secondary"
        [disabled]="!generatedTemplateId || testingQuery"
        (click)="testQuery()">
        <tui-icon icon="mat::play_arrow"></tui-icon>
        Test Template
      </button>
    </div>

    <!-- Test Results -->
    <div *ngIf="testResults" class="test-results">
      <h3>
        <tui-icon icon="mat::assessment"></tui-icon>
        Test Results
      </h3>

      <tui-notification appearance="success" class="success-notification">
        Template test completed successfully! Found
        {{ testResults.recordCount }} records in
        {{ testResults.executionTime }}ms.
      </tui-notification>
    </div>

    <!-- Step Actions -->
    <div class="step-actions">
      <button tuiButton appearance="secondary" (click)="showStep(3)">
        <tui-icon icon="mat::arrow_back"></tui-icon>
        Back: Generate Template
      </button>
      <button
        tuiButton
        appearance="primary"
        (click)="
          showAlert('🎉 Configuration completed successfully!', 'success')
        ">
        <tui-icon icon="mat::celebration"></tui-icon>
        Finish Configuration
      </button>
    </div>
  </div>
</div>
